import { ReportActionLogEntryResponse } from "../../../../client";

export type ActionType = ReportActionLogEntryResponse["action_type"];

export const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  EMAIL_RECEIVED: "Email Received",
  REPORT_CREATED: "Report Created",
  EMAIL_ADDED_TO_REPORT: "Email Added to Report",
  EMAIL_REJECTED_INVALID_SENDER: "Email Rejected (Invalid Sender)",
  EMAIL_REJECTED_DUPLICATE: "Email Rejected (Duplicate)",
  EMAIL_REJECTED_NO_ATTACHMENTS: "Email Rejected (No Attachments)",
  EMAIL_REJECTED_REPLY_THREAD: "Email Rejected (Reply Thread)",
  UPDATE_REPORT: "Report Updated",
  UPDATE_ADDRESSES: "Addresses Updated",
  INTEGRATION_JOB_ENQUEUED: "Integration Job Enqueued",
  INTEGRATION_JOB_STARTED: "Integration Job Started",
  INTEGRATION_JOB_COMPLETED: "Integration Job Completed",
  INTEGRATION_JOB_FAILED: "Integration Job Failed",
  CLEARANCE_EMAIL_SENT: "Clearance Email Sent",
  QUOTE_INDICATION_DRAFTED: "Quote Indication Drafted",
  ATTACHMENT_UPLOADED: "Attachment Uploaded",

  COMMENT_ADDED: "Comment Added",
};

export const ACTION_TYPE_ICONS: Record<ActionType, string> = {
  EMAIL_RECEIVED: "pi pi-envelope",
  REPORT_CREATED: "pi pi-file",
  EMAIL_ADDED_TO_REPORT: "pi pi-link",
  EMAIL_REJECTED_INVALID_SENDER: "pi pi-ban",
  EMAIL_REJECTED_DUPLICATE: "pi pi-copy",
  EMAIL_REJECTED_NO_ATTACHMENTS: "pi pi-file-excel",
  EMAIL_REJECTED_REPLY_THREAD: "pi pi-reply",
  UPDATE_REPORT: "pi pi-pencil",
  UPDATE_ADDRESSES: "pi pi-map-marker",
  INTEGRATION_JOB_ENQUEUED: "pi pi-clock",
  INTEGRATION_JOB_STARTED: "pi pi-spinner",
  INTEGRATION_JOB_COMPLETED: "pi pi-check-circle",
  INTEGRATION_JOB_FAILED: "pi pi-times-circle",
  CLEARANCE_EMAIL_SENT: "pi pi-send",
  QUOTE_INDICATION_DRAFTED: "pi pi-file-edit",
  ATTACHMENT_UPLOADED: "pi pi-paperclip",

  COMMENT_ADDED: "pi pi-comment",
};

type TagSeverity = "success" | "info" | "warning" | "danger" | null | undefined;

export const ACTION_TYPE_SEVERITY: Record<ActionType, TagSeverity> = {
  EMAIL_RECEIVED: "info",
  REPORT_CREATED: "success",
  EMAIL_ADDED_TO_REPORT: "info",
  EMAIL_REJECTED_INVALID_SENDER: "danger",
  EMAIL_REJECTED_DUPLICATE: "warning",
  EMAIL_REJECTED_NO_ATTACHMENTS: "warning",
  EMAIL_REJECTED_REPLY_THREAD: "warning",
  UPDATE_REPORT: "info",
  UPDATE_ADDRESSES: "info",
  INTEGRATION_JOB_ENQUEUED: null,
  INTEGRATION_JOB_STARTED: "info",
  INTEGRATION_JOB_COMPLETED: "success",
  INTEGRATION_JOB_FAILED: "danger",
  CLEARANCE_EMAIL_SENT: "success",
  QUOTE_INDICATION_DRAFTED: "info",
  ATTACHMENT_UPLOADED: "info",

  COMMENT_ADDED: "info",
};

/** Append "Z" if the timestamp lacks a timezone so the browser treats it as UTC. */
const ensureUtc = (ts: string) => (ts.endsWith("Z") ? ts : ts + "Z");

export const formatTimestamp = (ts: string) => {
  const date = new Date(ensureUtc(ts));
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const formatLabel = (actionType: ActionType) =>
  ACTION_TYPE_LABELS[actionType] ?? actionType;

export const formatMetadata = (
  metadata: Record<string, unknown> | null
): string => {
  if (!metadata) return "";
  const parts: string[] = [];
  if (metadata.integration_type) {
    parts.push(`Type: ${metadata.integration_type}`);
  }
  if (metadata.updated_fields && Array.isArray(metadata.updated_fields)) {
    parts.push(`Fields: ${(metadata.updated_fields as string[]).join(", ")}`);
  }
  if (metadata.address_count) {
    parts.push(`Addresses: ${metadata.address_count}`);
  }
  if (metadata.comment_preview) {
    parts.push(`"${(metadata.comment_preview as string).slice(0, 60)}..."`);
  }
  if (metadata.email_subject) {
    parts.push(`Subject: ${metadata.email_subject}`);
  }
  if (metadata.sender) {
    parts.push(`From: ${metadata.sender}`);
  }
  if (metadata.reason) {
    parts.push(`Reason: ${metadata.reason}`);
  }
  if (metadata.error_code) {
    parts.push(`Error: ${metadata.error_code}`);
  }
  if (metadata.status) {
    parts.push(`Status: ${metadata.status}`);
  }
  if (metadata.recipients && Array.isArray(metadata.recipients)) {
    parts.push(`Recipients: ${(metadata.recipients as string[]).join(", ")}`);
  }
  if (metadata.filenames && Array.isArray(metadata.filenames)) {
    parts.push(`Files: ${(metadata.filenames as string[]).join(", ")}`);
  }
  if (metadata.classification) {
    parts.push(`Classification: ${metadata.classification}`);
  }
  if (metadata.source) {
    parts.push(`Source: ${metadata.source}`);
  }
  if (metadata.draft_url) {
    parts.push(`Draft URL: ${metadata.draft_url}`);
  }
  return parts.join(" | ");
};
